import React, { useState, useEffect } from 'react';
import axios from 'axios';
import DashboardLayout from '@/components/DashboardLayout';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Textarea } from '@/components/ui/textarea';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Badge } from '@/components/ui/badge';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { toast } from 'sonner';
import { Truck, Package, Plus, Trash2, Edit2, CheckCircle2, Clock, ClipboardList, Shield } from 'lucide-react';
import { format } from 'date-fns';

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;

const getAuthHeader = () => {
  const token = localStorage.getItem('token');
  return { Authorization: `Bearer ${token}` };
};

const NATURE_OPTIONS = [
  { value: "Purchase-RMT/FNG", label: "Purchase - RMT/FNG (Raw Material / Finished Goods)" },
  { value: "Purchase-CTM", label: "Purchase - CTM (Consumables / Tools / Machinery)" },
  { value: "Import Purchase", label: "Import Purchase (International / BOE Based)" },
  { value: "Jobwork-Own", label: "Jobwork - Own (Our material returning from jobber)" },
  { value: "Jobwork-Client", label: "Jobwork - Client (Client's material for our jobwork)" },
  { value: "Sales Return", label: "Sales Return (Goods returned by customer)" },
  { value: "Samples Returned", label: "Samples Returned (Our samples coming back)" },
  { value: "Samples Received", label: "Samples Received (New samples from others)" }
];

// Mapping from location codes to unit codes for document numbering
const LOCATION_TO_UNIT_MAP = {
  'ASSEMBLY': 'GE1',
  'METAL': 'GE2', 
  'WOOD': 'GE3'
};

// Fallback unit options (used if locations API fails)
const DEFAULT_UNIT_OPTIONS = [
  { value: "GE1", label: "Assembly Unit", location_id: null },
  { value: "GE2", label: "Metal Work Unit", location_id: null },
  { value: "GE3", label: "Wood Work Unit", location_id: null }
];

const statusColors = {
  pending: 'bg-yellow-100 text-yellow-800 border-yellow-200',
  received: 'bg-green-100 text-green-800 border-green-200',
  rejected: 'bg-red-100 text-red-800 border-red-200'
};

const statusLabels = {
  pending: 'Pending',
  received: 'Received',
  rejected: 'Rejected'
};

// Auto-caps input handler
const toUpperCase = (value) => value ? value.toUpperCase() : '';

export default function GateEntryPage() {
  const [loading, setLoading] = useState(false);
  const [suppliers, setSuppliers] = useState([]);
  const [gateEntries, setGateEntries] = useState([]);
  const [pendingPOs, setPendingPOs] = useState([]);
  const [openDialog, setOpenDialog] = useState(false);
  const [editMode, setEditMode] = useState(false);
  const [editingEntry, setEditingEntry] = useState(null);
  const [activeTab, setActiveTab] = useState('form');
  const [unitOptions, setUnitOptions] = useState(DEFAULT_UNIT_OPTIONS);
  
  const [formData, setFormData] = useState({
    unit_code: 'GE1',
    unit_location_id: '',
    supplier_id: '',
    supplier_name: '',
    invoice_dc_no: '',
    nature_of_inward: '',
    vehicle_no: '',
    driver_name: '',
    driver_contact: '',
    remarks: '',
    items: [{ po_id: '', po_number: '', item_id: '', item_name: '', quantity: '', unit: 'NOS', num_packages: '', remarks: '' }]
  });

  // Fetch locations and map to unit options
  const fetchLocations = async () => {
    try {
      const response = await axios.get(`${API_URL}/locations`, { headers: getAuthHeader() });
      const locations = response.data || [];
      
      if (locations.length > 0) {
        const mappedOptions = locations.map(loc => ({
          value: LOCATION_TO_UNIT_MAP[loc.code] || `GE-${loc.code}`,
          label: loc.name,
          location_id: loc.id,
          location_code: loc.code
        }));
        setUnitOptions(mappedOptions);
        
        // Set default to first location
        if (mappedOptions.length > 0 && !formData.unit_location_id) {
          setFormData(prev => ({
            ...prev,
            unit_code: mappedOptions[0].value,
            unit_location_id: mappedOptions[0].location_id
          }));
        }
      }
    } catch (error) {
      console.error('Failed to fetch locations:', error);
      // Keep default options
    }
  };

  useEffect(() => {
    fetchSuppliers();
    fetchLocations();
    fetchGateEntries();
  }, []);

  const fetchSuppliers = async () => {
    try {
      const response = await axios.get(`${API_URL}/suppliers`, { headers: getAuthHeader() });
      setSuppliers(response.data);
    } catch (error) {
      console.error('Failed to fetch suppliers:', error);
    }
  };

  const fetchGateEntries = async () => {
    try {
      const response = await axios.get(`${API_URL}/gate-entries?created_by=mine`, { headers: getAuthHeader() });
      setGateEntries(response.data);
    } catch (error) {
      console.error('Failed to fetch gate entries:', error);
    }
  };

  const fetchPendingPOs = async (supplierId) => {
    if (!supplierId) {
      setPendingPOs([]);
      return;
    }
    try {
      const response = await axios.get(`${API_URL}/gate-entries/supplier/${supplierId}/pending-pos`, { headers: getAuthHeader() });
      setPendingPOs(response.data);
    } catch (error) {
      console.error('Failed to fetch pending POs:', error);
      setPendingPOs([]);
    }
  };

  const handleSupplierChange = (supplierId) => {
    const supplier = suppliers.find(s => s.id === supplierId);
    setFormData({
      ...formData,
      supplier_id: supplierId,
      supplier_name: supplier?.name || '',
      items: [{ po_id: '', po_number: '', item_id: '', item_name: '', quantity: '', unit: 'NOS', num_packages: '', remarks: '' }]
    });
    fetchPendingPOs(supplierId);
  };

  const handlePOChange = (itemIndex, poId) => {
    const newItems = [...formData.items];
    
    if (poId === 'NA') {
      newItems[itemIndex] = {
        ...newItems[itemIndex],
        po_id: null,
        po_number: 'NA',
        item_id: null,
        item_name: '',
        quantity: '',
        unit: 'NOS'
      };
    } else {
      const selectedPO = pendingPOs.find(p => p.po_id === poId);
      newItems[itemIndex] = {
        ...newItems[itemIndex],
        po_id: poId,
        po_number: selectedPO?.po_number || '',
        item_id: '',
        item_name: '',
        quantity: '',
        unit: 'NOS'
      };
    }
    
    setFormData({ ...formData, items: newItems });
  };

  const handleItemChange = (itemIndex, itemId) => {
    const newItems = [...formData.items];
    const po = pendingPOs.find(p => p.po_id === newItems[itemIndex].po_id);
    const selectedItem = po?.pending_items.find(i => i.item_id === itemId);
    
    if (selectedItem) {
      newItems[itemIndex] = {
        ...newItems[itemIndex],
        item_id: itemId,
        item_name: selectedItem.item_name,
        quantity: selectedItem.pending_qty,
        unit: selectedItem.unit || 'NOS'
      };
      setFormData({ ...formData, items: newItems });
    }
  };

  const updateItem = (index, field, value) => {
    const newItems = [...formData.items];
    newItems[index] = { ...newItems[index], [field]: value };
    setFormData({ ...formData, items: newItems });
  };

  const addItem = () => {
    setFormData({
      ...formData,
      items: [...formData.items, { po_id: '', po_number: '', item_id: '', item_name: '', quantity: '', unit: 'NOS', num_packages: '', remarks: '' }]
    });
  };

  const removeItem = (index) => {
    if (formData.items.length <= 1) {
      toast.error('At least one item is required');
      return;
    }
    const newItems = formData.items.filter((_, i) => i !== index);
    setFormData({ ...formData, items: newItems });
  };

  const resetForm = () => {
    setFormData({
      unit_code: 'GE1',
      supplier_id: '',
      supplier_name: '',
      invoice_dc_no: '',
      nature_of_inward: '',
      vehicle_no: '',
      driver_name: '',
      driver_contact: '',
      remarks: '',
      items: [{ po_id: '', po_number: '', item_id: '', item_name: '', quantity: '', unit: 'NOS', num_packages: '', remarks: '' }]
    });
    setPendingPOs([]);
    setEditMode(false);
    setEditingEntry(null);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.supplier_name || !formData.nature_of_inward) {
      toast.error('Please fill all required fields');
      return;
    }

    // Validate items
    const validItems = formData.items.filter(item => item.item_name && item.quantity > 0);
    if (validItems.length === 0) {
      toast.error('Please add at least one item with name and quantity');
      return;
    }

    setLoading(true);
    try {
      const payload = {
        ...formData,
        items: validItems.map(item => ({
          po_id: item.po_id || null,
          po_number: item.po_number || 'NA',
          item_id: item.item_id || null,
          item_name: item.item_name,
          quantity: parseFloat(item.quantity) || 0,
          unit: item.unit || 'NOS',
          num_packages: item.num_packages ? parseInt(item.num_packages) : null,
          remarks: item.remarks || null
        }))
      };

      if (editMode && editingEntry) {
        await axios.patch(`${API_URL}/gate-entries/${editingEntry.id}`, payload, { headers: getAuthHeader() });
        toast.success('Gate Entry updated successfully!');
      } else {
        await axios.post(`${API_URL}/gate-entries`, payload, { headers: getAuthHeader() });
        toast.success('Gate Entry created successfully!');
      }
      
      resetForm();
      fetchGateEntries();
      setActiveTab('register');
    } catch (error) {
      console.error('Error saving gate entry:', error);
      toast.error(error.response?.data?.detail || 'Failed to save gate entry');
    } finally {
      setLoading(false);
    }
  };

  const handleEdit = (entry) => {
    if (entry.status !== 'pending') {
      toast.error('Cannot edit entry after store has processed it');
      return;
    }
    
    setEditMode(true);
    setEditingEntry(entry);
    setFormData({
      unit_code: entry.unit_code || 'GE1',
      supplier_id: entry.supplier_id || '',
      supplier_name: entry.supplier_name || entry.supplier_name_raw || '',
      invoice_dc_no: entry.invoice_dc_no || '',
      nature_of_inward: entry.nature_of_inward || '',
      vehicle_no: entry.vehicle_no || '',
      driver_name: entry.driver_name || '',
      driver_contact: entry.driver_contact || '',
      remarks: entry.remarks || '',
      items: entry.items?.length > 0 ? entry.items.map(item => ({
        ...item,
        num_packages: item.num_packages || ''
      })) : [{ po_id: '', po_number: '', item_id: '', item_name: '', quantity: '', unit: 'NOS', num_packages: '', remarks: '' }]
    });
    
    if (entry.supplier_id) {
      fetchPendingPOs(entry.supplier_id);
    }
    setActiveTab('form');
  };

  const handleDelete = async (entry) => {
    if (entry.status !== 'pending') {
      toast.error('Cannot delete entry after store has processed it');
      return;
    }
    
    if (!window.confirm(`Are you sure you want to delete ${entry.gate_entry_no}?`)) {
      return;
    }

    try {
      await axios.delete(`${API_URL}/gate-entries/${entry.id}`, { headers: getAuthHeader() });
      toast.success('Gate Entry deleted successfully!');
      fetchGateEntries();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete gate entry');
    }
  };

  const getPOItemsForDropdown = (poId) => {
    if (!poId || poId === 'NA') return [];
    const po = pendingPOs.find(p => p.po_id === poId);
    return po?.pending_items || [];
  };

  return (
    <DashboardLayout>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold flex items-center gap-2">
              <Shield className="w-7 h-7 text-blue-600" />
              Security Gate Entry
            </h1>
            <p className="text-muted-foreground">Record incoming materials at the security gate</p>
          </div>
        </div>

        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <TabsList>
            <TabsTrigger value="form" className="flex items-center gap-2">
              <Truck className="w-4 h-4" />
              Entry Form
            </TabsTrigger>
            <TabsTrigger value="register" className="flex items-center gap-2">
              <ClipboardList className="w-4 h-4" />
              Register ({gateEntries.length})
            </TabsTrigger>
          </TabsList>

          {/* Entry Form Tab */}
          <TabsContent value="form">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Truck className="w-5 h-5 text-blue-600" />
                  {editMode ? 'Edit Gate Entry' : 'New Gate Entry'}
                </CardTitle>
                <CardDescription>
                  {editMode ? `Editing ${editingEntry?.gate_entry_no}` : 'Record details of incoming vehicle and materials'}
                </CardDescription>
              </CardHeader>
              <CardContent>
                <form onSubmit={handleSubmit} className="space-y-6">
                  {/* Basic Info Row */}
                  <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                    <div className="space-y-2">
                      <Label>Unit *</Label>
                      <Select 
                        value={formData.unit_code} 
                        onValueChange={(v) => {
                          const selectedUnit = unitOptions.find(opt => opt.value === v);
                          setFormData({ 
                            ...formData, 
                            unit_code: v,
                            unit_location_id: selectedUnit?.location_id || ''
                          });
                        }}
                      >
                        <SelectTrigger>
                          <SelectValue placeholder="Select unit" />
                        </SelectTrigger>
                        <SelectContent>
                          {unitOptions.map(opt => (
                            <SelectItem key={opt.value} value={opt.value}>{opt.label}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>

                    <div className="space-y-2">
                      <Label>Nature of Inward *</Label>
                      <Select value={formData.nature_of_inward} onValueChange={(v) => setFormData({ ...formData, nature_of_inward: v })}>
                        <SelectTrigger>
                          <SelectValue placeholder="Select nature" />
                        </SelectTrigger>
                        <SelectContent>
                          {NATURE_OPTIONS.map(opt => (
                            <SelectItem key={opt.value} value={opt.value}>{opt.label}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>

                    <div className="space-y-2">
                      <Label>Vehicle No.</Label>
                      <Input
                        value={formData.vehicle_no}
                        onChange={(e) => setFormData({ ...formData, vehicle_no: e.target.value.toUpperCase() })}
                        placeholder="MH-12-AB-1234"
                      />
                    </div>

                    <div className="space-y-2">
                      <Label>Invoice/DC No.</Label>
                      <Input
                        value={formData.invoice_dc_no}
                        onChange={(e) => setFormData({ ...formData, invoice_dc_no: toUpperCase(e.target.value) })}
                        placeholder="INV-001"
                        className="uppercase"
                      />
                    </div>
                  </div>

                  {/* Supplier Row */}
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label>Supplier *</Label>
                      <Select value={formData.supplier_id} onValueChange={handleSupplierChange}>
                        <SelectTrigger>
                          <SelectValue placeholder="Select supplier" />
                        </SelectTrigger>
                        <SelectContent>
                          {suppliers.map(s => (
                            <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>

                    <div className="space-y-2">
                      <Label>Or Enter Supplier Name Manually</Label>
                      <Input
                        value={formData.supplier_name}
                        onChange={(e) => setFormData({ ...formData, supplier_name: toUpperCase(e.target.value), supplier_id: '' })}
                        placeholder="Enter supplier name if not in list"
                        className="uppercase"
                      />
                    </div>
                  </div>

                  {/* Driver Info Row */}
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label>Driver Name</Label>
                      <Input
                        value={formData.driver_name}
                        onChange={(e) => setFormData({ ...formData, driver_name: toUpperCase(e.target.value) })}
                        placeholder="Driver name"
                        className="uppercase"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label>Driver Contact</Label>
                      <Input
                        value={formData.driver_contact}
                        onChange={(e) => setFormData({ ...formData, driver_contact: e.target.value })}
                        placeholder="Phone number"
                      />
                    </div>
                  </div>

                  {/* Items Section */}
                  <div className="space-y-3">
                    <div className="flex items-center justify-between">
                      <Label className="text-base font-semibold">Items ({formData.items.length})</Label>
                      <Button type="button" variant="outline" size="sm" onClick={addItem}>
                        <Plus className="w-4 h-4 mr-1" /> Add Item
                      </Button>
                    </div>

                    <div className="border rounded-lg overflow-hidden">
                      <Table>
                        <TableHeader>
                          <TableRow className="bg-muted/50">
                            <TableHead className="w-[180px]">PO No.</TableHead>
                            <TableHead className="w-[220px]">Item Name *</TableHead>
                            <TableHead className="w-[100px]">Qty *</TableHead>
                            <TableHead className="w-[80px]">Unit</TableHead>
                            <TableHead className="w-[80px]">Packages</TableHead>
                            <TableHead>Remarks</TableHead>
                            <TableHead className="w-[50px]"></TableHead>
                          </TableRow>
                        </TableHeader>
                        <TableBody>
                          {formData.items.map((item, idx) => (
                            <TableRow key={idx}>
                              <TableCell>
                                <Select
                                  value={item.po_id || (item.po_number === 'NA' ? 'NA' : '')}
                                  onValueChange={(v) => handlePOChange(idx, v)}
                                >
                                  <SelectTrigger className="h-9">
                                    <SelectValue placeholder="Select PO" />
                                  </SelectTrigger>
                                  <SelectContent>
                                    <SelectItem value="NA">NA - No PO</SelectItem>
                                    {pendingPOs.map(po => (
                                      <SelectItem key={po.po_id} value={po.po_id}>
                                        {po.po_number} ({po.pending_items.length} items)
                                      </SelectItem>
                                    ))}
                                  </SelectContent>
                                </Select>
                              </TableCell>
                              <TableCell>
                                {item.po_id && item.po_id !== 'NA' ? (
                                  <Select
                                    value={item.item_id || ''}
                                    onValueChange={(v) => handleItemChange(idx, v)}
                                  >
                                    <SelectTrigger className="h-9">
                                      <SelectValue placeholder="Select item" />
                                    </SelectTrigger>
                                    <SelectContent>
                                      {getPOItemsForDropdown(item.po_id).map(poItem => (
                                        <SelectItem key={poItem.item_id} value={poItem.item_id}>
                                          {poItem.item_name} (Pending: {poItem.pending_qty})
                                        </SelectItem>
                                      ))}
                                    </SelectContent>
                                  </Select>
                                ) : (
                                  <Input
                                    value={item.item_name}
                                    onChange={(e) => updateItem(idx, 'item_name', toUpperCase(e.target.value))}
                                    placeholder="Enter item name"
                                    className="h-9 uppercase"
                                  />
                                )}
                              </TableCell>
                              <TableCell>
                                <Input
                                  type="number"
                                  value={item.quantity}
                                  onChange={(e) => updateItem(idx, 'quantity', e.target.value)}
                                  placeholder="0"
                                  className="h-9 w-20"
                                />
                              </TableCell>
                              <TableCell>
                                <Input
                                  value={item.unit}
                                  onChange={(e) => updateItem(idx, 'unit', toUpperCase(e.target.value))}
                                  placeholder="NOS"
                                  className="h-9 w-16 uppercase"
                                />
                              </TableCell>
                              <TableCell>
                                <Input
                                  type="number"
                                  value={item.num_packages || ''}
                                  onChange={(e) => updateItem(idx, 'num_packages', e.target.value)}
                                  placeholder="0"
                                  className="h-9 w-20"
                                />
                              </TableCell>
                              <TableCell>
                                <Input
                                  value={item.remarks || ''}
                                  onChange={(e) => updateItem(idx, 'remarks', toUpperCase(e.target.value))}
                                  placeholder="Remarks"
                                  className="h-9 uppercase"
                                />
                              </TableCell>
                              <TableCell>
                                <Button
                                  type="button"
                                  variant="ghost"
                                  size="sm"
                                  onClick={() => removeItem(idx)}
                                  disabled={formData.items.length <= 1}
                                >
                                  <Trash2 className="w-4 h-4 text-destructive" />
                                </Button>
                              </TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </div>
                  </div>

                  {/* Remarks */}
                  <div className="space-y-2">
                    <Label>General Remarks</Label>
                    <Textarea
                      value={formData.remarks}
                      onChange={(e) => setFormData({ ...formData, remarks: toUpperCase(e.target.value) })}
                      placeholder="Any additional notes..."
                      rows={2}
                      className="uppercase"
                    />
                  </div>

                  {/* Actions */}
                  <div className="flex justify-end gap-3">
                    {editMode && (
                      <Button type="button" variant="outline" onClick={resetForm}>
                        Cancel Edit
                      </Button>
                    )}
                    <Button type="submit" disabled={loading} className="bg-blue-600 hover:bg-blue-700">
                      {loading ? 'Saving...' : editMode ? 'Update Entry' : 'Submit Entry'}
                    </Button>
                  </div>
                </form>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Register Tab */}
          <TabsContent value="register">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <ClipboardList className="w-5 h-5 text-blue-600" />
                  Gate Entry Register
                </CardTitle>
                <CardDescription>
                  View all entries you have recorded
                </CardDescription>
              </CardHeader>
              <CardContent>
                <div className="border rounded-lg overflow-hidden">
                  <Table>
                    <TableHeader>
                      <TableRow className="bg-muted/50">
                        <TableHead>Entry No.</TableHead>
                        <TableHead>Date</TableHead>
                        <TableHead>Supplier</TableHead>
                        <TableHead>Nature</TableHead>
                        <TableHead>Items</TableHead>
                        <TableHead>Vehicle</TableHead>
                        <TableHead className="text-center">Status</TableHead>
                        <TableHead className="text-right">Actions</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {gateEntries.length === 0 ? (
                        <TableRow>
                          <TableCell colSpan={8} className="text-center py-12">
                            <Package className="w-12 h-12 mx-auto mb-4 text-muted-foreground opacity-50" />
                            <p className="text-muted-foreground">No entries recorded yet</p>
                            <p className="text-sm text-muted-foreground mt-1">Use the Entry Form to record incoming materials</p>
                          </TableCell>
                        </TableRow>
                      ) : (
                        gateEntries.map((entry) => (
                          <TableRow key={entry.id}>
                            <TableCell>
                              <Badge variant="outline" className="font-mono">{entry.gate_entry_no}</Badge>
                            </TableCell>
                            <TableCell className="text-muted-foreground text-sm">
                              {entry.entry_date ? format(new Date(entry.entry_date), 'dd/MM/yyyy HH:mm') : '-'}
                            </TableCell>
                            <TableCell className="font-medium">
                              {entry.supplier_name || entry.supplier_name_raw || '-'}
                            </TableCell>
                            <TableCell className="text-sm">
                              {entry.nature_of_inward || '-'}
                            </TableCell>
                            <TableCell>
                              {entry.items?.length > 0 ? (
                                <div className="space-y-1">
                                  {entry.items.slice(0, 2).map((item, i) => (
                                    <div key={i} className="text-sm">
                                      <span className="font-medium">{item.item_name}</span>
                                      <span className="text-muted-foreground ml-1">({item.quantity} {item.unit})</span>
                                      {item.po_number && item.po_number !== 'NA' && (
                                        <Badge variant="outline" className="ml-1 text-xs">{item.po_number}</Badge>
                                      )}
                                    </div>
                                  ))}
                                  {entry.items.length > 2 && (
                                    <span className="text-xs text-muted-foreground">+{entry.items.length - 2} more</span>
                                  )}
                                </div>
                              ) : (
                                <span className="text-muted-foreground text-sm">{entry.num_packages || 1} package(s)</span>
                              )}
                            </TableCell>
                            <TableCell className="text-sm text-muted-foreground">
                              {entry.vehicle_no || '-'}
                            </TableCell>
                            <TableCell className="text-center">
                              <Badge className={statusColors[entry.status] || statusColors.pending}>
                                {entry.status === 'pending' && <Clock className="w-3 h-3 mr-1" />}
                                {entry.status === 'received' && <CheckCircle2 className="w-3 h-3 mr-1" />}
                                {statusLabels[entry.status] || entry.status}
                              </Badge>
                            </TableCell>
                            <TableCell className="text-right">
                              {entry.status === 'pending' ? (
                                <div className="flex items-center justify-end gap-1">
                                  <Button
                                    size="sm"
                                    variant="ghost"
                                    onClick={() => handleEdit(entry)}
                                  >
                                    <Edit2 className="w-4 h-4" />
                                  </Button>
                                  <Button
                                    size="sm"
                                    variant="ghost"
                                    onClick={() => handleDelete(entry)}
                                  >
                                    <Trash2 className="w-4 h-4 text-destructive" />
                                  </Button>
                                </div>
                              ) : (
                                <span className="text-xs text-muted-foreground">Locked</span>
                              )}
                            </TableCell>
                          </TableRow>
                        ))
                      )}
                    </TableBody>
                  </Table>
                </div>
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </div>
    </DashboardLayout>
  );
}
